import { Tag } from "antd"
import type React from "react"

import { globalMapStatusStyle } from "./helpers/status-style.ts"
import type { GlobalMapListDisplayRow } from "./redux/global-map.types.ts"

// Subcontractor/Appointment statuses come from vocabularies
// (apps.subcontractor.models.Subcontractor.Status /
// apps.calendars.models.EventStatus) that `status-style.ts`'s Lead/Project
// palettes were never meant to cover — a small, deliberately SEPARATE color
// map for the handful of labels this app's Global Map List actually shows
// for these two entities, not a second implementation of
// `globalMapStatusStyle`'s own Lead/Project resolution logic.
const OTHER_ENTITY_STATUS_STYLE: Record<string, React.CSSProperties> = {
  Active: {
    color: "#15803D",
    backgroundColor: "#F0FDF4",
    borderColor: "#BBF7D0"
  },
  Scheduled: {
    color: "#1D4ED8",
    backgroundColor: "#EFF6FF",
    borderColor: "#BFDBFE"
  },
  Completed: {
    color: "#0F766E",
    backgroundColor: "#F0FDFA",
    borderColor: "#99F6E4"
  },
  Cancelled: {
    color: "#B91C1C",
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA"
  }
}

// Same neutral treatment `status-style.ts` itself falls back to for an
// unrecognized Lead/Project label — reused here for a Subcontractor/
// Appointment label this map doesn't happen to name, rather than a second,
// differently-colored "unknown status" convention.
const OTHER_ENTITY_FALLBACK_STYLE: React.CSSProperties = {
  color: "#1D4ED8",
  backgroundColor: "#EFF6FF",
  borderColor: "#BFDBFE"
}

interface StatusPillProps {
  entityType: GlobalMapListDisplayRow["entityType"]
  statusLabel?: string
}

/**
 * The Status column's badge, reused across all 4 Global Map List entities.
 * For Lead/Project it calls the real, already-shipped `globalMapStatusStyle`
 * helper (Task 21) — it does NOT reimplement that color resolution here.
 */
export function StatusPill({ entityType, statusLabel }: StatusPillProps) {
  if (!statusLabel) return null

  const style =
    entityType === "lead" || entityType === "project"
      ? globalMapStatusStyle(entityType, statusLabel)
      : (OTHER_ENTITY_STATUS_STYLE[statusLabel] ?? OTHER_ENTITY_FALLBACK_STYLE)

  return <Tag style={style}>{statusLabel}</Tag>
}

export default StatusPill
